import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Typography,
  Autocomplete,
  useTheme,
} from "@mui/material";
import { AddCircleOutlineOutlined, Delete } from "@mui/icons-material";
import CustomTextField from "../../../components/common/Textfield";
import CustomSelect from "../../../components/common/Select";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { Venue } from "../../../types/venue";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import { IStreamFormData } from "../../../types/streams";

interface Deceased {
  id: number;
  salutation: string | null;
  first_name: string | null;
  second_name: string | null;
  surname: string | null;
  nickname: string | null;
  gender: string | null;
  date_of_birth: string | null;
  date_of_death: string | null;
  relationship_to_deceased: string | null;
  religion: string | null;
  denomination: string | null;
  person_image: string | null;
}

interface Booking {
  id: number;
  deceased: Deceased[];
  service?: string;
  events?: Array<{
    title?: string | null;
  }>;
  title: string;
}

interface AddStreamDetailsProps {
  formData: IStreamFormData;
  handleInputChange: (field: keyof IStreamFormData, value: string) => void;
  isEdit: boolean;
}

const AddStreamDetails: React.FC<AddStreamDetailsProps> = ({
  formData,
  handleInputChange,
  isEdit,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venueSearchTerm, setVenueSearchTerm] = useState("");
  const [venueLoading, setVenueLoading] = useState(false);
  const [bookingOptions, setBookingOptions] = useState<Booking[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [bookingSearchTerm, setBookingSearchTerm] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);

  const [newVenue, setNewVenue] = useState<Venue>({
    name: "",
    address_one: "",
    address_two: "",
    suburb: "",
    city: "",
  });

  // Debounced fetch for venues
  useEffect(() => {
    const timer = setTimeout(() => {
      if (venueSearchTerm.length >= 3) {
        fetchVenues(venueSearchTerm);
      } else {
        setVenueOptions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [venueSearchTerm]);

  // Debounced fetch for bookings
  useEffect(() => {
    const timer = setTimeout(() => {
      if (bookingSearchTerm.length >= 3) {
        fetchBookings(bookingSearchTerm);
      } else {
        setBookingOptions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [bookingSearchTerm]);

  // Load initial selected venue
  useEffect(() => {
    const loadSelectedVenue = async () => {
      if (
        formData.venue &&
        (!selectedVenue || selectedVenue.id !== formData.venue)
      ) {
        setVenueLoading(true);
        try {
          const response = await apiService.get<Venue>(
            `/api/venues/venue/${formData.venue}/`,
          );
          setSelectedVenue(response.data);
        } catch (err) {
          console.error("Failed to fetch selected venue:", err);
          toast.error("Failed to load selected venue. Please try again.");
        } finally {
          setVenueLoading(false);
        }
      } else if (!formData.venue && selectedVenue) {
        setSelectedVenue(null);
      }
    };
    loadSelectedVenue();
  }, [formData.venue, selectedVenue]);

  // Load initial selected booking
  useEffect(() => {
    const loadSelectedBooking = async () => {
      if (
        formData.booking &&
        (!selectedBooking || selectedBooking.id !== formData.booking)
      ) {
        setBookingLoading(true);
        try {
          const response = await apiService.get<Booking>(
            `/api/bookings/booking/${formData.booking}/`,
          );
          setSelectedBooking(response.data);
          console.log(response.data);
        } catch (err) {
          console.error("Failed to fetch selected booking:", err);
          toast.error("Failed to load selected booking. Please try again.");
        } finally {
          setBookingLoading(false);
        }
      } else if (!formData.booking && selectedBooking) {
        setSelectedBooking(null);
      }
    };
    loadSelectedBooking();
    console.log(formData.booking);
  }, [formData.booking, selectedBooking]);

  // Calculate travel duration
  useEffect(() => {
    if (formData.travellingFrom && formData.travellingTo) {
      const fromTime = new Date(`1970-01-01T${formData.travellingFrom}:00`);
      const toTime = new Date(`1970-01-01T${formData.travellingTo}:00`);
      let diffInMinutes = (toTime.getTime() - fromTime.getTime()) / (1000 * 60);
      if (diffInMinutes < 0) {
        diffInMinutes += 24 * 60; // Add 24 hours in minutes
      }
      const roundedMinutes = Math.round(diffInMinutes);
      handleInputChange("travelDuration", String(roundedMinutes));
    } else {
      handleInputChange("travelDuration", "");
    }
  }, [formData.travellingFrom, formData.travellingTo, handleInputChange]);

  const fetchVenues = async (search: string) => {
    setVenueLoading(true);
    try {
      const response = await apiService.get<{ results: Venue[] }>(
        `/api/venues/venue/?search=${search}`,
      );
      setVenueOptions(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch venues:", err);
      toast.error("Failed to load venues. Please try again.");
    } finally {
      setVenueLoading(false);
    }
  };

  const fetchBookings = async (search: string) => {
    setBookingLoading(true);
    try {
      const response = await apiService.get<{ results: Booking[] }>(
        `/api/bookings/booking/?search=${search}`,
      );
      setBookingOptions(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
      toast.error("Failed to load bookings. Please try again.");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleVenueChange = (
    event: React.SyntheticEvent,
    newValue: Venue | null,
  ) => {
    setSelectedVenue(newValue);
    handleInputChange("venue", newValue ? String(newValue.id) : "");
  };

  const handleBookingChange = (
    event: React.SyntheticEvent,
    newValue: Booking | null,
  ) => {
    setSelectedBooking(newValue);
    handleInputChange("booking", newValue ? String(newValue.id) : "");
  };

  const handleNewVenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewVenue((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddVenue = async () => {
    setLoading(true);
    try {
      const response = await apiService.post<Venue>(
        "/api/venues/venue/",
        newVenue,
      );
      setVenueOptions((prev) => [...prev, response.data]);
      setSelectedVenue(response.data);
      handleInputChange("venue", String(response.data.id));
      toast.success("Venue added successfully");
      setOpen(false);
      setNewVenue({
        name: "",
        address_one: "",
        address_two: "",
        suburb: "",
        city: "",
      });
    } catch (err: unknown) {
      console.error("Failed to add venue:", err);
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to add venue. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVenue = async () => {
    if (!selectedVenue?.id) return;
    setIsDeleting(true);
    try {
      await apiService.delete(`/api/venues/venue/${selectedVenue.id}/`);
      setVenueOptions((prev) =>
        prev.filter((venue) => venue.id !== selectedVenue.id),
      );
      setSelectedVenue(null);
      handleInputChange("venue", "");
      toast.success("Venue deleted successfully");
      setDeleteModalOpen(false);
    } catch (err: unknown) {
      console.error("Failed to delete venue:", err);
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to delete venue. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const normalizeYoutubeUrl = (url: string): string | null => {
    try {
      const parsedUrl = new URL(url);

      // Standard watch URL
      if (parsedUrl.hostname.includes("youtube.com")) {
        // ?v=VIDEO_ID
        const v = parsedUrl.searchParams.get("v");
        if (v) {
          return `https://www.youtube.com/watch?v=${v}`;
        }

        // /live/VIDEO_ID
        const liveMatch = parsedUrl.pathname.match(/\/live\/([\w-]{11})/);
        if (liveMatch) {
          return `https://www.youtube.com/watch?v=${liveMatch[1]}`;
        }
      }

      // youtu.be/VIDEO_ID
      if (parsedUrl.hostname === "youtu.be") {
        const id = parsedUrl.pathname.replace("/", "");
        if (id.length === 11) {
          return `https://www.youtube.com/watch?v=${id}`;
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const [youtubeError, setYoutubeError] = useState("");

  const getBookingLabel = (option: Booking) => {
    const serviceType = option.service;

    if (
      (serviceType === "Funeral" || serviceType === "Funeral_Home") &&
      option.title &&
      option.title !== "N/A"
    ) {
      return option.title;
    }

    if (
      (serviceType === "Church" ||
        serviceType === "Other" ||
        serviceType === "Conference") &&
      option.events?.[0]?.title &&
      option.events[0].title !== "N/A"
    ) {
      return option.events[0].title;
    }

    if (option.deceased && option.deceased.length > 0) {
      const fullNames = option.deceased
        .map((d) =>
          [d.salutation, d.first_name, d.second_name, d.surname]
            .filter(Boolean)
            .join(" "),
        )
        .join(", ");
      return fullNames || "Untitled Booking";
    }

    return "Untitled Booking";
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            fullWidth
            id="title"
            label="Stream Title"
            value={formData.title || ""}
            onChange={(e) => handleInputChange("title", e.target.value)}
            error={!formData.title && isEdit}
            helperText={
              !formData.title && isEdit ? "Stream Title is required" : ""
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth>
            <Autocomplete
              options={bookingOptions}
              getOptionLabel={getBookingLabel}
              value={selectedBooking}
              onChange={handleBookingChange}
              onInputChange={(event, newInputValue) => {
                setBookingSearchTerm(newInputValue);
              }}
              loading={bookingLoading}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Select Booking"
                  helperText={
                    !formData.booking
                      ? "Type at least 3 characters to search bookings (required)"
                      : "Type at least 3 characters to search bookings"
                  }
                  error={!formData.booking}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {bookingLoading && (
                          <CircularProgress
                            size={20}
                            sx={{
                              color: colors.primary[400],
                            }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: colors.neutral[300],
                      },
                      "&:hover fieldset": {
                        borderColor: colors.neutral[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.primary[400],
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.neutral[300],
                    },
                    "& .MuiInputBase-input": {
                      color: colors.neutral[100],
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth>
            <CustomSelect
              labelId="service-type-label"
              id="serviceType"
              value={formData.serviceType}
              label="Type of Service"
              onChange={(e) =>
                handleInputChange("serviceType", e.target.value as string)
              }
              error={!formData.serviceType}
            >
              <MenuItem value="Funeral">Funeral</MenuItem>
              <MenuItem value="Memorial">Memorial</MenuItem>
              <MenuItem value="Celebration">Celebration of Life</MenuItem>
              <MenuItem value="Burial">Burial</MenuItem>
              <MenuItem value="Church">Church Service</MenuItem>
              <MenuItem value="Conference">Conference</MenuItem>
            </CustomSelect>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            fullWidth
            id="startTime"
            label="Start Time"
            type="time"
            value={formData.startTime}
            onChange={(e) => handleInputChange("startTime", e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!formData.startTime}
            helperText={!formData.startTime ? "Start Time is required" : ""}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            fullWidth
            id="endTime"
            label="End Time"
            type="time"
            value={formData.endTime}
            onChange={(e) => handleInputChange("endTime", e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!formData.endTime}
            helperText={!formData.endTime ? "End Time is required" : ""}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <CustomTextField
            fullWidth
            id="date"
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange("date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={!formData.date}
            helperText={!formData.date ? "Date is required" : ""}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Autocomplete
              fullWidth
              options={venueOptions}
              getOptionLabel={(option: Venue) => option.name || ""}
              value={selectedVenue}
              onChange={handleVenueChange}
              onInputChange={(event, newInputValue) => {
                setVenueSearchTerm(newInputValue);
              }}
              loading={venueLoading}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Select Venue"
                  helperText={
                    !formData.venue
                      ? "Type at least 3 characters to search venues (required)"
                      : "Type at least 3 characters to search venues"
                  }
                  error={!formData.venue}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {venueLoading && (
                          <CircularProgress
                            size={20}
                            sx={{
                              color: colors.primary[400],
                            }}
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: colors.neutral[300],
                      },
                      "&:hover fieldset": {
                        borderColor: colors.neutral[400],
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: colors.primary[400],
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: colors.neutral[300],
                    },
                    "& .MuiInputBase-input": {
                      color: colors.neutral[100],
                    },
                  }}
                />
              )}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: colors.primary[400],
                  "&:hover": {
                    backgroundColor: colors.neutral[800],
                  },
                }}
              >
                <AddCircleOutlineOutlined sx={{ mr: 0.5 }} />
                <Typography sx={{ color: colors.neutral[100] }}>
                  Add Venue
                </Typography>
              </IconButton>
              {selectedVenue && (
                <IconButton
                  onClick={() => setDeleteModalOpen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: colors.primary[400],
                    "&:hover": {
                      backgroundColor: colors.neutral[800],
                    },
                  }}
                >
                  <Delete sx={{ mr: 0.5 }} />
                  <Typography sx={{ color: colors.neutral[100] }}>
                    Delete Venue
                  </Typography>
                </IconButton>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            fullWidth
            label="YouTube Link"
            value={formData.youtube_stream_link || ""}
            error={Boolean(youtubeError)}
            helperText={youtubeError}
            onChange={(e) => {
              const rawValue = e.target.value;
              const normalizedUrl = normalizeYoutubeUrl(rawValue);

              if (!rawValue) {
                setYoutubeError("");
                handleInputChange("youtube_stream_link", "");
                return;
              }

              if (!normalizedUrl) {
                setYoutubeError("Please enter a valid YouTube link");
              } else {
                setYoutubeError("");
                handleInputChange("youtube_stream_link", normalizedUrl);
              }
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            fullWidth
            label="Go Fund Me"
            value={formData.go_fund_me_link || ""}
            onChange={(e) =>
              handleInputChange("go_fund_me_link", e.target.value)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            fullWidth
            label="Website Link"
            value={formData.website_stream_link || ""}
            onChange={(e) =>
              handleInputChange("website_stream_link", e.target.value)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <CustomTextField
            fullWidth
            id="specialNotes"
            label="Special Notes"
            multiline
            rows={4}
            value={formData.specialNotes}
            onChange={(e) => handleInputChange("specialNotes", e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Time Allocation
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 2 }}>
            <CustomTextField
              fullWidth
              id="callTime"
              label="Call Time"
              type="time"
              value={formData.callTime}
              onChange={(e) => handleInputChange("callTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <CustomTextField
              fullWidth
              id="travellingFrom"
              label="Travelling From"
              type="time"
              value={formData.travellingFrom}
              onChange={(e) =>
                handleInputChange("travellingFrom", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <CustomTextField
              fullWidth
              id="travellingTo"
              label="Travelling To"
              type="time"
              value={formData.travellingTo}
              onChange={(e) =>
                handleInputChange("travellingTo", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <CustomTextField
              fullWidth
              id="setupFrom"
              label="Setup From"
              type="time"
              value={formData.setupFrom}
              onChange={(e) => handleInputChange("setupFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <CustomTextField
              fullWidth
              id="setupTo"
              label="Setup To"
              type="time"
              value={formData.setupTo}
              onChange={(e) => handleInputChange("setupTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <CustomTextField
              fullWidth
              id="estimatedWrap"
              label="Estimated Wrap"
              type="time"
              value={formData.estimatedWrap}
              onChange={(e) =>
                handleInputChange("estimatedWrap", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <CustomTextField
              fullWidth
              id="travelDuration"
              label="Travel Duration in minutes"
              value={formData.travelDuration ?? ""}
              disabled
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: colors.neutral[100],
                  opacity: 1,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
      {formData.serviceType !== "Conference" &&
        formData.serviceType !== "Church" && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Additional Information (Optional)
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 3 }}>
                <CustomTextField
                  fullWidth
                  id="priest_name"
                  label="Name of Priest"
                  value={formData.priest_name}
                  onChange={(e) =>
                    handleInputChange("priest_name", e.target.value)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <CustomTextField
                  fullWidth
                  id="mc_name"
                  label="MC Name"
                  value={formData.mc_name}
                  onChange={(e) => handleInputChange("mc_name", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <CustomTextField
                  fullWidth
                  id="eulogist_name"
                  label="Eulogist"
                  value={formData.eulogist_name}
                  onChange={(e) =>
                    handleInputChange("eulogist_name", e.target.value)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <CustomTextField
                  fullWidth
                  id="other"
                  label="Other"
                  value={formData.other}
                  onChange={(e) => handleInputChange("other", e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.neutral[900],
            color: colors.neutral[100],
          }}
        >
          Add New Venue
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: colors.neutral[900],
          }}
        >
          <CustomTextField
            fullWidth
            label="Venue Name"
            name="name"
            value={newVenue.name}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            error={!newVenue.name}
            helperText={!newVenue.name ? "Venue Name is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.neutral[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.neutral[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Address One"
            name="address_one"
            value={newVenue.address_one}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            error={!newVenue.address_one}
            helperText={!newVenue.address_one ? "Address One is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.neutral[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.neutral[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Address Two"
            name="address_two"
            value={newVenue.address_two}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.neutral[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.neutral[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />
          <CustomTextField
            fullWidth
            label="Suburb"
            name="suburb"
            value={newVenue.suburb}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            required
            error={!newVenue.suburb}
            helperText={!newVenue.suburb ? "Suburb is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.neutral[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.neutral[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          />

          <CustomTextField
            fullWidth
            label="City"
            name="city"
            value={newVenue.city}
            onChange={handleNewVenueChange}
            variant="outlined"
            margin="normal"
            select
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: colors.primary[300],
                },
                "&:hover fieldset": {
                  borderColor: colors.neutral[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary[400],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.primary[300],
              },
              "& .MuiInputBase-input": {
                color: colors.neutral[100],
              },
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {reduxTheme.cities.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </CustomTextField>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: colors.neutral[900],
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            sx={{
              color: colors.primary[100],
              borderColor: colors.primary[100],
              backgroundColor: colors.primary[800],
              "&:hover": {
                borderColor: colors.neutral[400],
                backgroundColor: colors.neutral[800],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddVenue}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
              "&:hover": {
                backgroundColor: colors.primary[500],
              },
              "&.Mui-disabled": {
                backgroundColor: colors.neutral[400],
                color: colors.primary[900],
              },
            }}
            variant="contained"
            disabled={
              loading ||
              !newVenue.name ||
              !newVenue.address_one ||
              !newVenue.suburb ||
              !newVenue.city
            }
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: colors.neutral[100] }} />
            ) : (
              "Add Venue"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.neutral[900],
            color: colors.neutral[100],
          }}
        >
          Confirm Delete Venue
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: colors.neutral[900],
          }}
        >
          <Typography>
            Are you sure you want to delete the venue &quot;
            {selectedVenue?.name}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: colors.neutral[900],
          }}
        >
          <Button
            onClick={() => setDeleteModalOpen(false)}
            sx={{
              color: colors.primary[100],
              borderColor: colors.primary[100],
              backgroundColor: colors.primary[800],
              "&:hover": {
                borderColor: colors.neutral[400],
                backgroundColor: colors.neutral[800],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteVenue}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
              "&:hover": {
                backgroundColor: colors.primary[500],
              },
              "&.Mui-disabled": {
                backgroundColor: colors.neutral[400],
                color: colors.primary[900],
              },
            }}
            variant="contained"
            disabled={isDeleting || !selectedVenue}
          >
            {isDeleting ? (
              <CircularProgress size={20} sx={{ color: colors.neutral[100] }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddStreamDetails;
